import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { logger } from '#~/logger/index.js';
import { withTimeout } from '#~/lib/with-timeout.js';

/**
 * Vendor-neutral cross-account bridge cleanup for the boot path.
 *
 * WHY THIS MODULE EXISTS: the ENG-4154 warm-account-switch recovery
 * (`bridge:unlink` → sidecar wipes its own SQLite credentials → reboot →
 * fresh-install link path) was born inside `AutoLinkController`
 * (`clearStaleCrossAccountLink`). That controller is fail-closed skipped on
 * every non-Iris vendor (`not_iris_vendor`, ENG-4367/4371), which left Hive
 * with the DETECTION half of the pattern (agents/get 403 → suppress
 * default-room bootstrap → ENG-4166 D3 boot-error screen) and no REMEDY: a
 * new account signing in on a machine whose bridge is linked under another
 * account wedged permanently on "This device is linked to a studio your
 * account can't open." with `database.db` surgery as the only escape.
 *
 * ENG-4685 (ENG-4483 ITB finding #004) showed Iris had the same gap in the
 * account-first boot: `access_empty` is decided only when the local identity
 * is `linked`, while the controller's `clearStaleCrossAccountLink` runs only
 * from the zero-Studio path, which requires `unlinked`. The two are mutually
 * exclusive by the decision table, so on Iris nobody owned the remedy and the
 * boot fell through to a generic "Unable to load account" with reload as the
 * only affordance. The boot path therefore owns this recovery on EVERY
 * Electron vendor; the controller's copy stays for its own (unlinked) path and
 * can never double-fire with this one.
 *
 * The unlink itself is NOT auto-link behavior: the sidecar unlinks the agent
 * in the registry with its OWN stored token (account-agnostic) and the
 * post-reboot destination is whatever the product's unlinked boot is: the
 * Bridge Setup picker or a silent create on Iris, the manual pair screen on
 * Hive. This module invokes no auto-link machinery.
 *
 * Per rules: reference-rules-in-comments, think-in-systems
 * (ai.knowledge/global).
 */

/**
 * Re-attempt window. An unlink that "succeeded" but did not actually clear
 * the registry link (registry weather) would otherwise reload into the same
 * forbidden state and try again forever — reload → detect → unlink → reload.
 * Within this window a repeat detection falls through to the boot-error
 * screen instead of looping. Persisted in localStorage (not sessionStorage)
 * because the sidecar-initiated recovery path restarts the whole app, which
 * discards sessionStorage.
 */
export const CROSS_ACCOUNT_UNLINK_ATTEMPT_TTL_MS = 5 * 60_000;

/**
 * Mirror of the controller's IPC ceiling: the main-side `bridge:unlink`
 * handler is itself bounded by the sidecar's 30s mailroom unlink timeout, so
 * anything past that is a hang, not a slow success.
 */
export const CROSS_ACCOUNT_UNLINK_IPC_TIMEOUT_MS = 35_000;

export const crossAccountUnlinkAttemptKey = (agentId: string): string =>
  `cross-account-unlink-attempted.${agentId}`;

/**
 * ENG-4750: the pending unlink per agent, shared by every bootstrap pass of
 * ONE launch. The account-first bootstrap generator re-runs whenever one of
 * its inputs settles (studio user, inventory, identity push), so a pass that
 * starts while the sidecar is still answering the FIRST pass's unlink decides
 * `access_empty` again, reads the latch that pass wrote milliseconds earlier,
 * and falls through to the boot-error screen. The unlink then completes and
 * the Bridge is in fact unlinked, which is why "Try Again" lands on the picker
 * (PROD replays 2026-09-22, four macOS launches: latch refusal 54–300 ms after
 * the request, `linked → unlinked` push ~100 ms later). The TTL latch exists
 * to stop reload LOOPS (attempt → reload → attempt), not to refuse a pass
 * that is merely concurrent with the attempt it latched, so such a pass joins
 * the pending attempt and gets its outcome. Module state, not storage: it must
 * not outlive the renderer that owns the pending IPC. Per rules:
 * settle-every-terminal-branch, reference-rules-in-comments
 * (ai.knowledge/global) — the entry clears on every settle of the run.
 */
const inFlightUnlinkByAgent = new Map<string, Promise<boolean>>();

/** @internal test-only reset of the per-launch in-flight map. */
export const __resetCrossAccountRecoveryForTest = (): void => {
  inFlightUnlinkByAgent.clear();
};

/** Narrow storage slice so tests inject a plain object, no jsdom required. */
export type AttemptStore = Pick<Storage, 'getItem' | 'setItem'>;

const readLastAttemptMs = (store: AttemptStore, agentId: string): number | undefined => {
  try {
    const raw = store.getItem(crossAccountUnlinkAttemptKey(agentId));
    const parsed = raw === null ? Number.NaN : Number(raw);

    return Number.isFinite(parsed) ? parsed : undefined;
  } catch {
    // Storage unavailable (private mode / corrupted profile): treat as "no
    // prior attempt" — a single extra unlink attempt is safer than a wedge.
    return undefined;
  }
};

const writeAttemptMs = (store: AttemptStore, agentId: string, nowMs: number): void => {
  try {
    store.setItem(crossAccountUnlinkAttemptKey(agentId), String(nowMs));
  } catch {
    // Best-effort: losing the latch risks one extra attempt, never a wedge.
  }
};

export interface CrossAccountRecoveryDecisionInput {
  isElectron: boolean;
  lastAttemptAtMs: number | undefined;
  nowMs: number;
}

/**
 * Pure decision: may the BOOT PATH attempt the cross-account unlink now? True
 * only when (a) we are in Electron (the sidecar exists) and (b) no attempt was
 * made within the TTL (loop protection). Vendor no longer matters: see the
 * module header (ENG-4685).
 */
export const shouldAttemptCrossAccountUnlinkRecovery = ({
  isElectron,
  lastAttemptAtMs,
  nowMs
}: CrossAccountRecoveryDecisionInput): boolean => {
  if (!isElectron) {
    return false;
  }

  return (
    lastAttemptAtMs === undefined || nowMs - lastAttemptAtMs >= CROSS_ACCOUNT_UNLINK_ATTEMPT_TTL_MS
  );
};

export interface AttemptCrossAccountUnlinkOptions {
  agentId: string;
  /** Injection seams for tests; production callers pass only `agentId`. */
  store?: AttemptStore;
  sendUnlinkRequest?: () => Promise<boolean>;
  ipcTimeoutMs?: number;
  nowMs?: number;
}

/**
 * Attempt the vendor-neutral cross-account unlink. Returns true iff the
 * sidecar confirmed the unlink — the caller then reloads and the fresh boot
 * converges on the product's unlinked path (Iris: Bridge Setup picker or
 * silent create; Hive: manual pair screen). Any false (off Electron, latched,
 * IPC failure, timeout) means the caller falls through to the cross-account
 * boot-error screen; this function never throws. A call that lands while an
 * earlier pass's unlink for the same agent is still in flight joins that
 * attempt (ENG-4750) instead of reading the latch it wrote.
 */
export const attemptCrossAccountUnlinkRecovery = async ({
  agentId,
  store = window.localStorage,
  sendUnlinkRequest = ElectronAPI.sendUnlinkRequest,
  ipcTimeoutMs = CROSS_ACCOUNT_UNLINK_IPC_TIMEOUT_MS,
  nowMs = Date.now()
}: AttemptCrossAccountUnlinkOptions): Promise<boolean> => {
  // ENG-4750: checked BEFORE the latch. The latch belongs to the pending
  // attempt; refusing on it here is exactly the interstitial error screen.
  const pending = inFlightUnlinkByAgent.get(agentId);
  if (pending) {
    logger.warning(
      `[cross-account-recovery] bridge ${agentId} unlink already requested by an earlier bootstrap pass of this launch — joining it instead of refusing on the latch`
    );
    return pending;
  }

  const eligible = shouldAttemptCrossAccountUnlinkRecovery({
    isElectron: isElectronApp(),
    lastAttemptAtMs: readLastAttemptMs(store, agentId),
    nowMs
  });

  if (!eligible) {
    const ttlMinutes = Math.round(CROSS_ACCOUNT_UNLINK_ATTEMPT_TTL_MS / 60_000);
    logger.warning(
      `[cross-account-recovery] bridge ${agentId} is linked under a Studio this account cannot access; not attempting sidecar unlink (${isElectronApp() ? `attempted within the last ${ttlMinutes} minutes` : 'not Electron'}) — falling through to the boot-error screen`
    );
    return false;
  }

  // Latch BEFORE the IPC: if the sidecar reboots the app mid-flight the
  // attempt must still count, or the next boot would immediately re-attempt.
  writeAttemptMs(store, agentId, nowMs);

  logger.warning(
    `[cross-account-recovery] bridge ${agentId} is linked under a Studio this account cannot access — requesting sidecar unlink so the reload converges on the unlinked boot path (Bridge Setup / pair screen)`
  );

  // Never rejects: the catch below folds every failure into `false`, so a
  // joined pass can await it bare.
  const run = withTimeout(sendUnlinkRequest(), ipcTimeoutMs, 'crossAccountUnlinkRecovery')
    .catch((error: unknown) => {
      logger.warning(`[cross-account-recovery] sidecar unlink request failed: ${String(error)}`);

      return false;
    })
    .then((unlinked) => {
      if (!unlinked) {
        logger.warning(
          '[cross-account-recovery] sidecar did not confirm the unlink — falling through to the boot-error screen'
        );
      }

      return unlinked;
    });

  inFlightUnlinkByAgent.set(agentId, run);
  try {
    return await run;
  } finally {
    // Only the run that registered itself may clear the slot; once it has
    // settled the TTL latch alone owns re-attempt protection.
    if (inFlightUnlinkByAgent.get(agentId) === run) {
      inFlightUnlinkByAgent.delete(agentId);
    }
  }
};
